import Link from 'next/link';

import {
  getKnowledgeArticleLink,
  getKnowledgeCategoryLink,
} from '../../utils/knowledge/knowledgeArticles';
import { KnowledgeLocale } from '../../utils/knowledge/knowledgeCopy';
import { KnowledgeChannel } from '../../utils/knowledge/types';
import {
  KnowledgeCategoryRow,
  KnowledgeIndexHeading,
  KnowledgeMainColumn,
  KnowledgePageLayout,
  KnowledgeTopicHeading,
  KnowledgeTopicItem,
  KnowledgeTopicLink,
  KnowledgeTopicList,
  KnowledgeTopicSection,
  KnowledgeViewAllLink,
} from './KnowledgeCentre.styles';
import { KnowledgeHelpSidebar } from './KnowledgeHelpSidebar';

const MAX_VISIBLE_ARTICLES = 10;

export interface KnowledgeCategoryView {
  name: string;
  slug: string;
  articles: { urlName: string; title: string }[];
}

interface KnowledgeHelpIndexProps {
  locale: KnowledgeLocale;
  channel: KnowledgeChannel;
  title: string;
  topArticles: { urlName: string; title: string }[];
  categories: KnowledgeCategoryView[];
}

export const KnowledgeHelpIndex = ({
  locale,
  channel,
  title,
  topArticles,
  categories,
}: KnowledgeHelpIndexProps) => {
  const channelProp = { $channel: channel };

  const rows: KnowledgeCategoryView[][] = [];
  for (let i = 0; i < categories.length; i += 2) {
    rows.push(categories.slice(i, i + 2));
  }

  return (
    <KnowledgePageLayout {...channelProp}>
      <KnowledgeMainColumn>
        <KnowledgeIndexHeading>{title}</KnowledgeIndexHeading>
        {rows.map((row, rowIndex) => (
          <KnowledgeCategoryRow key={rowIndex}>
            {row.map((category) => {
              const visible = category.articles.slice(0, MAX_VISIBLE_ARTICLES);
              const hasMore = category.articles.length > MAX_VISIBLE_ARTICLES;

              return (
                <KnowledgeTopicSection key={category.slug}>
                  <KnowledgeTopicHeading>
                    <Link href={getKnowledgeCategoryLink(locale, channel, category.slug)}>
                      <span>{category.name}</span>
                    </Link>
                  </KnowledgeTopicHeading>
                  <KnowledgeTopicList>
                    {visible.map(({ urlName, title: articleTitle }) => (
                      <KnowledgeTopicItem key={urlName}>
                        <KnowledgeTopicLink
                          href={getKnowledgeArticleLink(locale, channel, urlName)}
                        >
                          <span>{articleTitle}</span>
                        </KnowledgeTopicLink>
                      </KnowledgeTopicItem>
                    ))}
                  </KnowledgeTopicList>
                  {hasMore ? (
                    <KnowledgeViewAllLink
                      {...channelProp}
                      href={getKnowledgeCategoryLink(locale, channel, category.slug)}
                    >
                      <span>{`View All (${category.articles.length})`}</span>
                    </KnowledgeViewAllLink>
                  ) : null}
                </KnowledgeTopicSection>
              );
            })}
          </KnowledgeCategoryRow>
        ))}
      </KnowledgeMainColumn>
      <KnowledgeHelpSidebar channel={channel} locale={locale} topArticles={topArticles} />
    </KnowledgePageLayout>
  );
};
